import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Search, Plus, Edit, Trash2, Shield, Users, Settings as SettingsIcon, FileText, Download, Mail, Clock, Send, KeyRound, Loader2, Bell } from 'lucide-react';
import TwoFactorCard from '@/components/TwoFactorCard';
import SessionAuditCard from '@/components/SessionAuditCard';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const availablePermissions = [
  'users.read', 'users.write', 'users.delete',
  'articles.read', 'articles.write', 'articles.delete',
  'tasks.read', 'tasks.write', 'tasks.delete',
  'reports.read', 'reports.write',
  'settings.read', 'settings.write',
  'admin.full'
];

const SettingsPage = () => {
  const navigate = useNavigate();
  const { token, user, setToken } = useAuth();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [isRoleOpen, setIsRoleOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [roleForm, setRoleForm] = useState({ name: '', permissions: [] });
  const [digestSchedule, setDigestSchedule] = useState({ enabled: false, recipient_email: '', day_of_week: 'monday', hour: 9 });
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [pwSaving, setPwSaving] = useState(false);

  const changePassword = async () => {
    if (!pwForm.current_password || !pwForm.new_password) {
      toast.error('Fill in both current and new password'); return;
    }
    if (pwForm.new_password !== pwForm.confirm_password) {
      toast.error('New password and confirmation do not match'); return;
    }
    setPwSaving(true);
    try {
      await axios.post(
        `${API}/auth/change-password`,
        { current_password: pwForm.current_password, new_password: pwForm.new_password },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPwForm({ current_password: '', new_password: '', confirm_password: '' });
      toast.success('Password updated successfully');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not change password');
    } finally { setPwSaving(false); }
  };

  useEffect(() => {
    fetchRoles();
    fetchDigestSchedule();
  }, []);

  const fetchRoles = async () => {
    try {
      const response = await axios.get(`${API}/roles`, {
        params: { search },
        headers: { Authorization: `Bearer ${token}` },
      });
      setRoles(response.data);
    } catch (error) {
      toast.error('Failed to fetch roles');
    } finally {
      setLoading(false);
    }
  };

  const fetchDigestSchedule = async () => {
    try {
      const res = await axios.get(`${API}/digest/schedule`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data) setDigestSchedule(res.data);
    } catch { /* silent */ }
  };

  const saveDigestSchedule = async () => {
    if (digestSchedule.enabled && !digestSchedule.recipient_email) {
      toast.error('Email address is required when scheduling is enabled');
      return;
    }
    setScheduleLoading(true);
    try {
      await axios.post(`${API}/digest/schedule`, digestSchedule, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Digest schedule saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save schedule');
    } finally {
      setScheduleLoading(false);
    }
  };

  const testDigestEmail = async () => {
    setTestingEmail(true);
    try {
      const res = await axios.post(`${API}/digest/schedule/test`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send test email');
    } finally {
      setTestingEmail(false);
    }
  };

  const handleSaveRole = async () => {
    if (!roleForm.name) {
      toast.error('Role name is required');
      return;
    }
    try {
      if (editMode && selectedRole) {
        await axios.put(`${API}/roles/${selectedRole.role_id}`, roleForm, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Role updated');
      } else {
        await axios.post(`${API}/roles`, roleForm, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Role created');
      }
      setIsRoleOpen(false);
      setRoleForm({ name: '', permissions: [] });
      setEditMode(false);
      fetchRoles();
    } catch (error) {
      toast.error('Failed to save role');
    }
  };

  const handleDeleteRole = async (roleId) => {
    if (!confirm('Delete this role?')) return;
    try {
      await axios.delete(`${API}/roles/${roleId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Role deleted');
      fetchRoles();
    } catch (error) {
      toast.error('Failed to delete role');
    }
  };

  const openEditRole = (role) => {
    setSelectedRole(role);
    setRoleForm({ name: role.name, permissions: role.permissions || [] });
    setEditMode(true);
    setIsRoleOpen(true);
  };

  const togglePermission = (permission) => {
    setRoleForm(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permission)
        ? prev.permissions.filter(p => p !== permission)
        : [...prev.permissions, permission]
    }));
  };

  const filteredRoles = roles.filter(r => 
    !search || r.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div data-testid="settings-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-[#0A0A0A]">Settings</h1>
          <p className="text-[#4B5563] text-sm">Manage roles and permissions</p>
        </div>
      </div>

      {/* User Profile Card */}
      <Card className="border-[#E5E7EB] mb-6">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Users className="w-5 h-5" />
            Your Profile
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <span className="text-sm text-[#9CA3AF]">Name</span>
              <p className="font-medium">{user?.name}</p>
            </div>
            <div>
              <span className="text-sm text-[#9CA3AF]">Email</span>
              <p className="font-medium">{user?.email}</p>
            </div>
            <div>
              <span className="text-sm text-[#9CA3AF]">Role</span>
              <p className="font-medium capitalize">{user?.role}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* User Management (admin/manager/hr only) */}
      {['admin', 'manager', 'hr'].includes(user?.role) && (
        <Card className="border-[#E5E7EB] mb-6" data-testid="user-management-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="w-5 h-5 text-[#002FA7]" />
              User Management
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-[#4B5563] mb-3">
              Add, invite, and manage user accounts, roles, and permissions for your organization.
            </p>
            <Button onClick={() => navigate('/users')} className="bg-[#002FA7] hover:bg-[#00227A]" data-testid="open-users-btn">
              Open User Management
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Notification preferences — all users */}
      <Card className="border-[#E5E7EB] mb-6" data-testid="notification-prefs-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Bell className="w-5 h-5 text-[#002FA7]" />
            Notification preferences
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-[#4B5563] mb-3">
            Mute DMs / mentions / rooms individually and set quiet hours for pushes.
          </p>
          <Button onClick={() => navigate('/settings/notifications')} variant="outline" data-testid="open-notification-prefs-btn">
            Open Notification Settings
          </Button>
        </CardContent>
      </Card>

      {/* Change Password */}
      <Card className="border-[#E5E7EB] mb-6" data-testid="change-password-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <KeyRound className="w-5 h-5" />
            Change Password
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-[#4B5563] mb-4">
            Rotate your account password. You'll stay logged in on this device; all pending password-reset links are invalidated.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
            <div>
              <Label className="text-xs">Current password</Label>
              <Input
                type="password"
                value={pwForm.current_password}
                onChange={e => setPwForm({ ...pwForm, current_password: e.target.value })}
                className="mt-1"
                data-testid="current-password-input"
              />
            </div>
            <div>
              <Label className="text-xs">New password (min 8)</Label>
              <Input
                type="password"
                value={pwForm.new_password}
                onChange={e => setPwForm({ ...pwForm, new_password: e.target.value })}
                className="mt-1"
                data-testid="new-password-input"
              />
            </div>
            <div>
              <Label className="text-xs">Confirm new password</Label>
              <Input
                type="password"
                value={pwForm.confirm_password}
                onChange={e => setPwForm({ ...pwForm, confirm_password: e.target.value })}
                className="mt-1"
                data-testid="confirm-password-input"
              />
            </div>
          </div>
          <Button
            onClick={changePassword}
            disabled={pwSaving || !pwForm.current_password || !pwForm.new_password}
            className="bg-[#002FA7] hover:bg-[#00227A]"
            data-testid="change-password-submit"
          >
            {pwSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />}
            Update password
          </Button>
        </CardContent>
      </Card>

      {/* Two-Factor Authentication */}
      <TwoFactorCard token={token} userEmail={user?.email} />

      {/* Session Audit Log */}
      <SessionAuditCard token={token} setToken={setToken} />


      {/* Documentation Downloads */}
      <Card className="border-[#E5E7EB] mb-6" data-testid="documentation-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Documentation
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-[#4B5563] mb-2">
            <strong>Live-generated</strong> from the running application — every endpoint, cron job, and MongoDB collection is introspected at download time, so these docs always reflect the code you're shipping.
          </p>
          {user?.role !== 'admin' && (
            <p className="text-xs text-[#FF9500] mb-3">Live regeneration requires admin role. Contact your admin for the latest PDFs.</p>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { kind: 'uml', title: 'UML Architecture', subtitle: 'System diagrams, ERD, module tree, scheduler jobs, security model', testid: 'uml' },
              { kind: 'quickref', title: 'Quick Reference', subtitle: 'API endpoints, integrations, troubleshooting, default creds', testid: 'quickref' },
              { kind: 'network', title: 'Network Diagram', subtitle: 'Public endpoints, internal topology, egress table, trust boundaries, data flows', testid: 'network' },
              { kind: 'security', title: 'Security Plan', subtitle: 'Threat model, auth, sessions, webhooks, secrets, compliance mapping (ISO 30401 / SOC-2 / NIST)', testid: 'security' },
            ].map(d => (
              <div key={d.kind} className="p-4 border border-[#E5E7EB] rounded-lg hover:border-[#002FA7] hover:bg-[#F0F4FF] transition-colors">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-[#002FA7]/10 flex items-center justify-center flex-shrink-0">
                    <FileText className="w-5 h-5 text-[#002FA7]" />
                  </div>
                  <div>
                    <p className="font-medium text-[#0A0A0A] text-sm">{d.title}</p>
                    <p className="text-xs text-[#9CA3AF]">{d.subtitle}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  {['pdf', 'md'].map(fmt => (
                    <button
                      key={fmt}
                      onClick={async () => {
                        try {
                          const ext = fmt === 'pdf' ? 'pdf' : 'md';
                          const res = await axios.get(`${API}/admin/docs/${d.kind}.${ext}`, {
                            headers: { Authorization: `Bearer ${token}` },
                            responseType: 'blob',
                          });
                          const blob = new Blob([res.data], { type: fmt === 'pdf' ? 'application/pdf' : 'text/markdown' });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `KStreak_${d.kind === 'uml' ? 'UML_Architecture' : d.kind === 'quickref' ? 'Quick_Reference' : d.kind === 'network' ? 'Network_Diagram' : 'Security_Plan'}.${ext}`;
                          document.body.appendChild(a);
                          a.click();
                          a.remove();
                          URL.revokeObjectURL(url);
                          toast.success(`${d.title} ${fmt.toUpperCase()} generated`);
                        } catch (e) {
                          toast.error(e.response?.status === 403 ? 'Admin access required' : 'Download failed');
                        }
                      }}
                      data-testid={`download-${d.testid}-${fmt}`}
                      className="flex-1 flex items-center justify-center gap-1.5 h-8 text-xs font-medium border border-[#002FA7] text-[#002FA7] rounded hover:bg-[#002FA7] hover:text-white transition-colors"
                    >
                      <Download className="w-3 h-3" />
                      {fmt.toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Digest Email Schedule */}
      <Card className="border-[#E5E7EB] mb-6" data-testid="digest-schedule-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Weekly Digest Email Schedule
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-[#4B5563] mb-4">Automatically send your AI-generated weekly digest to an email address on a schedule.</p>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Checkbox
                id="digest-enabled"
                checked={digestSchedule.enabled}
                onCheckedChange={(v) => setDigestSchedule({ ...digestSchedule, enabled: v })}
                data-testid="digest-schedule-toggle"
              />
              <Label htmlFor="digest-enabled" className="text-sm font-medium">Enable weekly digest email</Label>
            </div>
            {digestSchedule.enabled && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pl-7">
                <div>
                  <Label className="text-xs text-[#9CA3AF]">Recipient Email</Label>
                  <Input
                    type="email"
                    value={digestSchedule.recipient_email}
                    onChange={(e) => setDigestSchedule({ ...digestSchedule, recipient_email: e.target.value })}
                    placeholder="you@example.com"
                    data-testid="digest-schedule-email"
                  />
                </div>
                <div>
                  <Label className="text-xs text-[#9CA3AF]">Day of Week</Label>
                  <Select value={digestSchedule.day_of_week} onValueChange={(v) => setDigestSchedule({ ...digestSchedule, day_of_week: v })}>
                    <SelectTrigger data-testid="digest-schedule-day"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(d => (
                        <SelectItem key={d} value={d} className="capitalize">{d.charAt(0).toUpperCase() + d.slice(1)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-[#9CA3AF]">Time (Hour, UTC)</Label>
                  <Select value={String(digestSchedule.hour)} onValueChange={(v) => setDigestSchedule({ ...digestSchedule, hour: parseInt(v) })}>
                    <SelectTrigger data-testid="digest-schedule-hour"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 24 }, (_, i) => (
                        <SelectItem key={i} value={String(i)}>{String(i).padStart(2, '0')}:00 UTC</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            <div className="flex gap-2 pl-7">
              <Button size="sm" className="bg-[#002FA7] hover:bg-[#00227A]" onClick={saveDigestSchedule} disabled={scheduleLoading} data-testid="save-schedule-btn">
                {scheduleLoading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" /> : <Mail className="w-3.5 h-3.5 mr-1.5" />}
                Save Schedule
              </Button>
              {digestSchedule.enabled && digestSchedule.recipient_email && (
                <Button size="sm" variant="outline" onClick={testDigestEmail} disabled={testingEmail} data-testid="test-digest-email-btn">
                  {testingEmail ? <div className="w-4 h-4 border-2 border-[#002FA7] border-t-transparent rounded-full animate-spin mr-2" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
                  Send Test Email
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Roles Management */}
      <Card className="border-[#E5E7EB]">
        <CardHeader className="border-b border-[#E5E7EB]">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <CardTitle className="text-lg flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Role Management
            </CardTitle>
            <Button 
              className="bg-[#002FA7] hover:bg-[#00227A]" 
              onClick={() => { setEditMode(false); setRoleForm({ name: '', permissions: [] }); setIsRoleOpen(true); }}
              data-testid="create-role-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Role
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-4">
          <div className="relative max-w-md mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
            <Input
              placeholder="Search roles..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="role-search"
            />
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-32"><div className="spinner"></div></div>
          ) : (
            <Table data-testid="roles-table">
              <TableHeader>
                <TableRow className="bg-[#F3F4F6]">
                  <TableHead>Role Name</TableHead>
                  <TableHead>Permissions</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRoles.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} className="text-center py-8 text-[#9CA3AF]">
                      No roles found. Create your first role.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRoles.map((role) => (
                    <TableRow key={role.role_id}>
                      <TableCell className="font-medium">{role.name}</TableCell>
                      <TableCell>
                        <div className="flex gap-1 flex-wrap">
                          {role.permissions?.slice(0, 3).map((p, i) => (
                            <Badge key={i} variant="secondary" className="text-xs">{p}</Badge>
                          ))}
                          {role.permissions?.length > 3 && (
                            <Badge variant="secondary" className="text-xs">+{role.permissions.length - 3}</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" onClick={() => openEditRole(role)} data-testid={`edit-role-${role.role_id}`}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="text-[#FF3B30]" onClick={() => handleDeleteRole(role.role_id)} data-testid={`delete-role-${role.role_id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Role Dialog */}
      <Dialog open={isRoleOpen} onOpenChange={setIsRoleOpen}>
        <DialogContent className="max-w-lg" data-testid="role-dialog">
          <DialogHeader>
            <DialogTitle>{editMode ? 'Edit' : 'Create'} Role</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Role Name *</Label>
              <Input
                value={roleForm.name}
                onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })}
                placeholder="e.g., Manager, Editor, Viewer"
                data-testid="role-name-input"
              />
            </div>
            <div>
              <Label className="mb-2 block">Permissions</Label>
              <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto p-2 border border-[#E5E7EB] rounded">
                {availablePermissions.map((perm) => (
                  <div key={perm} className="flex items-center gap-2">
                    <Checkbox
                      id={perm}
                      checked={roleForm.permissions.includes(perm)}
                      onCheckedChange={() => togglePermission(perm)}
                    />
                    <label htmlFor={perm} className="text-sm cursor-pointer">{perm}</label>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRoleOpen(false)}>Cancel</Button>
            <Button className="bg-[#002FA7] hover:bg-[#00227A]" onClick={handleSaveRole} data-testid="save-role-btn">
              {editMode ? 'Update' : 'Create'} Role
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SettingsPage;
